const ENHANCED = 'data-ts-select-enhanced';

type EnhancedSelect = HTMLSelectElement & { __tsSync?: () => void };

let openPanel: HTMLElement | null = null;
let openButton: HTMLButtonElement | null = null;
let initialized = false;

const closePanel = (): void => {
  openPanel?.remove();
  openPanel = null;
  openButton?.setAttribute('aria-expanded', 'false');
  openButton = null;
};

const enhance = (select: EnhancedSelect): void => {
  // El <select> del mes es UI interna de flatpickr. Si se envuelve, su panel
  // se monta en <body> y flatpickr lo toma como clic externo: cerraba el
  // calendario al elegir un mes. Se deja el select nativo con su estilo.
  if (select.closest('.flatpickr-calendar')) return;
  if (select.hasAttribute(ENHANCED) || select.multiple || Number(select.size) > 1) return;
  select.setAttribute(ENHANCED, '');

  const shell = document.createElement('span');
  shell.className = 'ts-select-shell';
  select.parentNode?.insertBefore(shell, select);
  shell.appendChild(select);
  select.classList.add('ts-native-select');

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'ts-select-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.innerHTML = '<span class="ts-select-value"></span><i class="bi bi-chevron-down" aria-hidden="true"></i>';
  shell.appendChild(trigger);

  const value = trigger.querySelector<HTMLElement>('.ts-select-value')!;
  const sync = (): void => {
    const option = select.selectedOptions[0] ?? select.options[select.selectedIndex];
    value.textContent = option?.textContent?.trim() || select.getAttribute('placeholder') || 'Seleccionar';
    trigger.disabled = select.disabled;
    trigger.classList.toggle('is-placeholder', !select.value);
    trigger.setAttribute('aria-label', select.getAttribute('aria-label') || value.textContent || 'Seleccionar opción');
    trigger.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || 'false');
  };
  select.__tsSync = sync;
  sync();

  const open = (): void => {
    if (select.disabled) return;
    closePanel();
    sync();
    const rect = trigger.getBoundingClientRect();
    const panel = document.createElement('div');
    panel.className = 'ts-select-panel';
    panel.setAttribute('role', 'listbox');
    panel.style.setProperty('--ts-select-left', `${Math.max(8, rect.left)}px`);
    const estimatedHeight = Math.min(320, (select.options.length * 42) + 12);
    const top = window.innerHeight - rect.bottom > 170
      ? rect.bottom + 6
      : Math.max(8, rect.top - estimatedHeight - 6);
    panel.style.setProperty('--ts-select-top', `${top}px`);
    panel.style.setProperty('--ts-select-width', `${Math.max(rect.width, 180)}px`);

    Array.from(select.options).forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ts-select-option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.selected));
      item.disabled = option.disabled;
      item.innerHTML = `<span></span>${option.selected ? '<i class="bi bi-check2" aria-hidden="true"></i>' : ''}`;
      item.querySelector('span')!.textContent = option.textContent?.trim() || '';
      item.addEventListener('click', () => {
        select.value = option.value;
        option.selected = true;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        closePanel();
        trigger.focus();
      });
      panel.appendChild(item);
    });

    document.body.appendChild(panel);
    openPanel = panel;
    openButton = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => panel.querySelector<HTMLButtonElement>('[aria-selected="true"]:not(:disabled),button:not(:disabled)')?.focus());
  };

  trigger.addEventListener('click', open);
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  });
  select.addEventListener('change', sync);
  select.addEventListener('input', sync);
};

const scan = (root: ParentNode = document): void => {
  if (root instanceof HTMLSelectElement) enhance(root as EnhancedSelect);
  root.querySelectorAll<EnhancedSelect>('select:not([multiple])').forEach(enhance);
  document.querySelectorAll<HTMLElement>('.ts-select-shell').forEach(shell => {
    if (!shell.querySelector('select')) shell.remove();
  });
};

/** Mejora todos los selects presentes y futuros, incluidos modales dinámicos. */
export function initSelectEnhancer(): void {
  if (initialized) return;
  initialized = true;
  scan();
  const observer = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node instanceof Element) scan(node);
    }));
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('pointerdown', event => {
    if (openPanel && !openPanel.contains(event.target as Node) && event.target !== openButton) closePanel();
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && openPanel) {
      const button = openButton;
      closePanel();
      button?.focus();
    }
  });
  window.addEventListener('resize', closePanel, { passive: true });
  document.addEventListener('scroll', event => {
    if (!openPanel?.contains(event.target as Node)) closePanel();
  }, true);

  // Angular puede cambiar selected/disabled como propiedades sin mutar
  // atributos; sincronización liviana mantiene la etiqueta siempre vigente.
  window.setInterval(() => {
    document.querySelectorAll<EnhancedSelect>(`select[${ENHANCED}]`).forEach(select => select.__tsSync?.());
  }, 400);
}
